import { useCallback, useEffect, useState } from 'react';
import { Col, Divider, Flex, Form, message, Row, Typography } from 'antd';
import { useStatsigClient } from '@statsig/react-bindings';
import { CustomButton, CustomInput } from '../../components';
import { useSpin } from '../../components/SpinContext';
import { useNavigate } from 'react-router-dom';
import { setAuthState } from '../../redux-toolkit/slice/authSlice';
import { Login, LoginModel, LoginWithOkta } from '../../apis/auth';
import { toStatsigUser, useToken } from '../../common/helper';
import { clearPreviewSession } from '../../common/helper/tokenStorage';
import { fetchTeamsDDL } from '../../redux-toolkit/slice/teamsSlice';
import {
  persistor,
  resetDashboardFilterStore,
} from '../../redux-toolkit/store';
import { useDispatch } from 'react-redux';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { fetchMenu } from '../../redux-toolkit/slice/menuSlice';
import { MenuItemData } from '@/apis/types';
import { useWebSocket } from '@/context/WebSocketContext';
import OktaLoginButton from '../../components/OktaLoginButton/OktaLoginButton';
import GoogleLoginButton from '../../components/GoogleLoginButton/GoogleLoginButton';
import MicrosoftLoginButton from '../../components/MicrosoftLoginButton/MicrosoftLoginButton';
import ExtensivPortalButton from '../../components/ExtensivPortalButton/ExtensivPortalButton';
import { useOktaRuntime } from '../../OktaProvider';
import { getThemeAccentColor } from '@/utils/themeColor';

const { Text } = Typography;

interface OktaTransaction {
  codeVerifier?: string;
  code_verifier?: string;
}

const LoginForm = () => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const { setUserId, removeToken, decodedUserToken, saveLoginUser } =
    useToken();
  const {
    isReady: isOktaReady,
    isEnabled: isOktaEnabled,
    oktaAuth,
  } = useOktaRuntime();
  const { webSocketService, setSelectedUserID } = useWebSocket() || {};
  const { client: statsigClient } = useStatsigClient();
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();

  // StatsigAppProvider constructs its client once at app mount, before login,
  // so it never learns about the real user on its own (see StatsigAppProvider.tsx).
  // Push the freshly logged-in user in here instead, right where it's decoded.
  const syncStatsigUser = useCallback(
    (decodedToken: ReturnType<typeof decodedUserToken>) => {
      statsigClient.updateUserAsync(toStatsigUser(decodedToken)).catch(() => {
        // Best-effort: gates simply keep evaluating against the previous user
        // if this fails.
      });
    },
    [statsigClient]
  );

  const clearStoredAuth = () => {
    removeToken();
    setUserId(null);
    setSelectedUserID?.(null);
    webSocketService?.disconnect?.();
    localStorage.removeItem('@refreshToken');
    // Never let a stale/leftover preview session (sessionStorage) shadow a
    // fresh real login/logout in this tab.
    clearPreviewSession();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSsoCallback =
      params.has('google') ||
      params.has('microsoft') ||
      (params.has('code') && params.has('state'));
    if (isSsoCallback) return;

    const isAuthenticated = localStorage.getItem('@token');
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [navigate]);

  // Handle Okta redirect callback only if user initiated via button
  useEffect(() => {
    if (!isOktaReady) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const initiated = sessionStorage.getItem('okta_login_initiated') === '1';
    const redirectUri = `${window.location.origin}/login`;

    const cleanupUrl = () => {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, document.title, url.toString());
    };

    const run = async () => {
      if (!code || !state || !initiated || !isOktaEnabled || !oktaAuth) return;
      try {
        showSpin();
        const tx = oktaAuth?.transactionManager?.load?.() as
          OktaTransaction | undefined;
        const codeVerifier = tx?.codeVerifier || tx?.code_verifier;
        if (!codeVerifier) {
          console.error('Missing code_verifier from Okta transaction storage');
          return;
        }

        const res = await LoginWithOkta({
          code,
          state,
          code_verifier: codeVerifier,
          redirect_uri: redirectUri,
        });

        if (res?.data?.is_success) {
          await persistor.flush();
          await persistor.purge();
          resetDashboardFilterStore();
          localStorage.removeItem('persist:root');
          // Never let a leftover preview session (sessionStorage) shadow the
          // fresh real login being established in this tab.
          clearPreviewSession();
          localStorage.setItem('@refreshToken', res?.data?.refresh || '');
          await saveLoginUser(res?.data?.access || '');
          dispatch(setAuthState(true));
          dispatch(fetchTeamsDDL(res?.data?.access || ''));

          let isOnboarded = false;
          const decodedToken = decodedUserToken();
          syncStatsigUser(decodedToken);
          const user = decodedToken?.role?.toLowerCase() === 'admin';
          if (decodedToken) {
            isOnboarded = decodedToken?.is_onboarded === true;
          }
          const menuResult = await dispatch(
            fetchMenu(res?.data?.access || '')
          ).unwrap();
          const menuItems = Array.isArray(menuResult)
            ? menuResult
            : menuResult?.items || [];
          if (menuItems?.length === 0) {
            message.error(
              "You don't have permission to access this application. Please contact your administrator."
            );
            removeToken();
            setUserId(null);
            setSelectedUserID(null);
            webSocketService.disconnect();
            persistor.flush().then(() => {
              persistor.purge();
              resetDashboardFilterStore();
              localStorage.clear();
            });
            return;
          }

          if (!isOnboarded && user) {
            navigate('/onboarding');
            return;
          }
          if (menuItems && Array.isArray(menuItems) && menuItems.length > 0) {
            const firstRoute = menuItems.find(
              (item: MenuItemData) =>
                item.route && typeof item.route === 'string'
            );
            if (firstRoute) navigate(`/${firstRoute.route}`);
          }
        }
      } catch (err) {
        console.error('Okta Login Error:', err);
      } finally {
        hideSpin();
        sessionStorage.removeItem('okta_login_initiated');
        cleanupUrl();
      }
    };

    run();
  }, [
    decodedUserToken,
    dispatch,
    hideSpin,
    isOktaEnabled,
    isOktaReady,
    navigate,
    oktaAuth,
    removeToken,
    saveLoginUser,
    setSelectedUserID,
    setUserId,
    showSpin,
    syncStatsigUser,
    webSocketService,
  ]);

  // Handle Google OAuth redirect callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleStatus = params.get('google');
    if (!googleStatus) return;

    const cleanupUrl = () => {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, document.title, url.toString());
    };

    const run = async () => {
      if (googleStatus === 'error') {
        clearStoredAuth();
        const detail = params.get('detail') || 'google_login_failed';
        const errorMessages: Record<string, string> = {
          user_not_found:
            'No account found for this Google email. Please contact your administrator.',
          company_user_not_found:
            'No account found for this Google email. Please contact your administrator.',
          account_inactive:
            'Your account is inactive. Please contact your administrator.',
          email_not_found:
            'Could not retrieve email from Google. Please try again.',
        };
        message.error(
          errorMessages[detail] || 'Google sign-in failed. Please try again.'
        );
        cleanupUrl();
        return;
      }

      if (googleStatus !== 'success') return;

      const googleAccess = params.get('access');
      const googleRefresh = params.get('refresh');
      if (!googleAccess || !googleRefresh) {
        clearStoredAuth();
        message.error('Google sign-in failed. Please try again.');
        cleanupUrl();
        return;
      }

      try {
        showSpin();
        clearStoredAuth();
        await persistor.flush();
        await persistor.purge();
        resetDashboardFilterStore();
        localStorage.removeItem('persist:root');
        // Never let a leftover preview session (sessionStorage) shadow the
        // fresh real login being established in this tab.
        clearPreviewSession();
        localStorage.setItem('@refreshToken', googleRefresh);
        await saveLoginUser(googleAccess);
        dispatch(setAuthState(true));
        dispatch(fetchTeamsDDL(googleAccess));

        const decodedToken = decodedUserToken();
        syncStatsigUser(decodedToken);
        const isAdmin = decodedToken?.role?.toLowerCase() === 'admin';
        const isOnboarded = decodedToken?.is_onboarded === true;

        const menuResult = await dispatch(fetchMenu(googleAccess)).unwrap();
        const menuItems = Array.isArray(menuResult)
          ? menuResult
          : menuResult?.items || [];
        if (menuItems?.length === 0) {
          message.error(
            "You don't have permission to access this application. Please contact your administrator."
          );
          removeToken();
          setUserId(null);
          setSelectedUserID(null);
          webSocketService.disconnect();
          persistor.flush().then(() => {
            persistor.purge();
            resetDashboardFilterStore();
            localStorage.clear();
          });
          return;
        }

        if (!isOnboarded && isAdmin) {
          navigate('/onboarding');
          return;
        }
        if (menuItems && Array.isArray(menuItems) && menuItems.length > 0) {
          const firstRoute = menuItems.find(
            (item: MenuItemData) => item.route && typeof item.route === 'string'
          );
          if (firstRoute) navigate(`/${firstRoute.route}`);
        }
      } catch (err) {
        console.error('Google Login Error:', err);
        clearStoredAuth();
        message.error('Google sign-in failed. Please try again.');
      } finally {
        hideSpin();
        cleanupUrl();
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Microsoft OAuth redirect callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const microsoftStatus = params.get('microsoft');
    if (!microsoftStatus) return;

    const cleanupUrl = () => {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, document.title, url.toString());
    };

    const run = async () => {
      if (microsoftStatus === 'error') {
        clearStoredAuth();
        const detail = params.get('detail') || 'microsoft_login_failed';
        const errorMessages: Record<string, string> = {
          origin_not_allowed:
            'Microsoft sign-in is not available for this portal.',
          user_not_found:
            'No account found for this Microsoft email. Please contact your administrator.',
          company_user_not_found:
            'No account found for this Microsoft email. Please contact your administrator.',
          account_inactive:
            'Your account is inactive. Please contact your administrator.',
          email_not_found:
            'Could not retrieve email from Microsoft. Please try again.',
        };
        message.error(
          errorMessages[detail] || 'Microsoft sign-in failed. Please try again.'
        );
        cleanupUrl();
        return;
      }

      if (microsoftStatus !== 'success') return;

      const microsoftAccess = params.get('access');
      const microsoftRefresh = params.get('refresh');
      if (!microsoftAccess || !microsoftRefresh) {
        clearStoredAuth();
        message.error('Microsoft sign-in failed. Please try again.');
        cleanupUrl();
        return;
      }

      try {
        showSpin();
        clearStoredAuth();
        await persistor.flush();
        await persistor.purge();
        resetDashboardFilterStore();
        localStorage.removeItem('persist:root');
        // Never let a leftover preview session (sessionStorage) shadow the
        // fresh real login being established in this tab.
        clearPreviewSession();
        localStorage.setItem('@refreshToken', microsoftRefresh);
        await saveLoginUser(microsoftAccess);
        dispatch(setAuthState(true));
        dispatch(fetchTeamsDDL(microsoftAccess));

        const decodedToken = decodedUserToken();
        syncStatsigUser(decodedToken);
        const isAdmin = decodedToken?.role?.toLowerCase() === 'admin';
        const isOnboarded = decodedToken?.is_onboarded === true;

        const menuResult = await dispatch(fetchMenu(microsoftAccess)).unwrap();
        const menuItems = Array.isArray(menuResult)
          ? menuResult
          : menuResult?.items || [];
        if (menuItems?.length === 0) {
          message.error(
            "You don't have permission to access this application. Please contact your administrator."
          );
          removeToken();
          setUserId(null);
          setSelectedUserID(null);
          webSocketService.disconnect();
          persistor.flush().then(() => {
            persistor.purge();
            resetDashboardFilterStore();
            localStorage.clear();
          });
          return;
        }

        if (!isOnboarded && isAdmin) {
          navigate('/onboarding');
          return;
        }
        if (menuItems && Array.isArray(menuItems) && menuItems.length > 0) {
          const firstRoute = menuItems.find(
            (item: MenuItemData) => item.route && typeof item.route === 'string'
          );
          if (firstRoute) navigate(`/${firstRoute.route}`);
        }
      } catch (err) {
        console.error('Microsoft Login Error:', err);
        clearStoredAuth();
        message.error('Microsoft sign-in failed. Please try again.');
      } finally {
        hideSpin();
        cleanupUrl();
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFinish = async (values: LoginModel) => {
    if (values && Object.keys(values).length > 0) {
      const model = values;
      model.email = model.email.toLowerCase();
      setIsFormValid(true);
      showSpin();
      try {
        const res = await Login(values);
        if (res?.data?.is_success) {
          await persistor.flush();
          await persistor.purge();
          resetDashboardFilterStore();
          localStorage.removeItem('persist:root');
          // Never let a leftover preview session (sessionStorage) shadow the
          // fresh real login being established in this tab.
          clearPreviewSession();
          localStorage.setItem('@refreshToken', res?.data?.refresh || '');
          await saveLoginUser(res?.data?.access || '');
          dispatch(setAuthState(true));
          dispatch(fetchTeamsDDL(res?.data?.access || ''));

          let isOnboarded = false;
          const decodedToken = decodedUserToken();
          syncStatsigUser(decodedToken);
          const user = decodedToken?.role?.toLowerCase() === 'admin';

          if (decodedToken) {
            isOnboarded = decodedToken?.is_onboarded === true;
          }

          const menuResult = await dispatch(
            fetchMenu(res?.data?.access || '')
          ).unwrap();
          const menuItems = Array.isArray(menuResult)
            ? menuResult
            : menuResult?.items || [];
          if (menuItems?.length === 0) {
            message.error(
              "You don't have permission to access this application. Please contact your administrator."
            );
            removeToken();
            setUserId(null);
            setSelectedUserID(null);
            webSocketService.disconnect();
            persistor.flush().then(() => {
              persistor.purge();
              resetDashboardFilterStore();
              localStorage.clear();
            });
            return;
          }

          if (!isOnboarded && user) {
            navigate('/onboarding');
            return;
          }

          if (menuItems && Array.isArray(menuItems) && menuItems.length > 0) {
            // const firstRoute = menuItems[1];
            const firstRoute = menuItems.find(
              (item: MenuItemData) =>
                item.route && typeof item.route === 'string'
            );
            if (firstRoute) {
              navigate(`/${firstRoute.route}`);
            }
          }
        } else {
          message.error(res?.data?.data?.message);
        }
      } catch (error) {
        console.error('Login Error:', error);
        if (error === 'NO_MENU_ITEMS') {
          message.error(
            "You don't have permission to access this application. Please contact your administrator."
          );
          removeToken();
          setUserId(null);
          setSelectedUserID(null);
          webSocketService.disconnect();
        }
      } finally {
        hideSpin();
        setIsFormValid(false);
      }
    }
  };

  const handleClick = () => {
    navigate('/signup');
  };
  const loginBtnColor = getThemeAccentColor() || '#000';
  return (
    <div className='login-form'>
      <Form
        layout='vertical'
        form={form}
        name='validateOnly'
        onFinish={onFinish}
      >
        <Row className='login-form-row'>
          <Col span={24}>
            <Flex vertical gap={4}>
              <p className='welcome-text'>Welcome back</p>
              <p className='welcome-sub-text'>
                Enter your credentials to login to your account.
              </p>
            </Flex>
          </Col>
          <Col span={24}>
            <Form.Item
              className='custom-input'
              label='Email'
              name='email'
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <CustomInput
                placeholder='Enter email address'
                border='0px'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='20px'
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              className='custom-input'
              label='Password'
              name='password'
              rules={[
                { required: true, message: 'Please enter your password' },
                {
                  type: 'string',
                  min: 6,
                  message: 'Password must be at least 6 characters long.',
                },
                {
                  type: 'string',
                  max: 20,
                  message: 'Password cannot exceed 20 characters.',
                },
              ]}
            >
              <CustomInput
                placeholder='Enter password'
                border='0px'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='20px'
                type='password'
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <CustomButton
              text='Log in'
              block={true}
              height='36px'
              bgColor={loginBtnColor}
              color='#fff'
              borderRadius='8px'
              fontSize='14px'
              fontWeight={500}
              isFormValid={isFormValid}
              htmlType='submit'
              marginTop='6px'
            />
          </Col>
          <Col span={24}>
            <Divider className='login-divider'>OR</Divider>
          </Col>
          <Col span={24}>
            <Flex vertical gap={16}>
              <OktaLoginButton />
              <GoogleLoginButton />
              <MicrosoftLoginButton />
            </Flex>
          </Col>
        </Row>
      </Form>

      <Row justify='center' align='middle' className='login-footer-row'>
        <Col>
          <p className='login-footer-text'>
            {"Don't have an account?"}{' '}
            <span onClick={handleClick} className='login-signup-link'>
              Sign up
            </span>
          </p>
        </Col>
      </Row>

      <Row justify='center' align='middle'>
        <Col>
          <Text
            className='login-forgot-link'
            onClick={() => navigate('/forgot-password')}
          >
            Forgot Password?
          </Text>
        </Col>
      </Row>

      <ExtensivPortalButton />
    </div>
  );
};

export default LoginForm;
